'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isUserLoggedIn } from '@/helpers/local-storage';
import { authUserQueryOptions } from '@/lib/auth/common/useSession';
import { buildAbsoluteRedirectUrl } from '@/utils/auth/common/auth-storage';
import { getUrlParameter } from '@/lib/utils/url-params';

/**
 * Adopts a session the Academy API established on its own (partner SSO's entire return leg:
 * the API validates the SAML assertion at its own ACS, sets the cookie, and redirects here —
 * so localStorage may still say "logged out" while the server disagrees).
 *
 * Must run on every page load, not once per tab, since the IdP can land the user on any page
 * — cheap to do because it rides the shared auth query every page already resolves once,
 * rather than probing on its own.
 */
export function PartnerSessionSync() {
  const queryClient = useQueryClient();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || didRunRef.current) return;
    didRunRef.current = true;

    // Failure first, before any signed-in gate — a failed login is worth reporting
    // regardless of who's currently signed in. API redirects with ?error=sso_failed&ref=<id>
    // (reason stays server-side); hand off to the partner-login-error UI, keeping the ref.
    if (getUrlParameter('error') === 'sso_failed') {
      const ref = getUrlParameter('ref');
      const query = new URLSearchParams({ partner_login_error: '1', ...(ref ? { ref } : {}) });
      window.location.replace(buildAbsoluteRedirectUrl(`/?${query.toString()}`));
      return;
    }

    // Already stored locally, nothing to adopt. Not a correctness claim: a stored/server
    // uid mismatch (e.g. member -> partner) is caught and re-synced by useAuthUser() instead.
    if (isUserLoggedIn()) return;

    (async () => {
      try {
        // Goes through the shared auth query (not a private fetch) so it reuses the same
        // cache entry/in-flight request useAuthUser() already resolves this page load.
        await queryClient.fetchQuery(authUserQueryOptions);

        // Only reload once localStorage actually holds the user — otherwise (private mode,
        // quota, still anonymous) this effect's own isUserLoggedIn() guard never trips.
        if (!isUserLoggedIn()) return;

        // Full reload, matching every other login path (see useLoginForm) — a soft update
        // would leave the app depending on cache/event timing instead.
        window.location.reload();
      } catch {
        // Network error — leave the user logged out rather than guessing.
      }
    })();
  }, [queryClient]);

  return null;
}
